import Supercluster from "supercluster";
import { toLonLat } from "ol/proj";

/**
 * Map Clustering Utility for Tracking Pages (Devices & Vehicles)
 *
 * Uses supercluster for efficient geospatial point clustering that works
 * across all 3 map providers: QIMap (OpenLayers), Mapbox, and Google Maps.
 *
 * Usage:
 *   const cluster = createTrackingCluster(points);
 *   const clusters = cluster.getClusters(bounds, zoom);
 */

// Default cluster options
const DEFAULT_OPTIONS = {
  radius: 60, // Cluster radius in pixels
  maxZoom: 16, // Max zoom level to cluster at (beyond this, all points are individual)
  minZoom: 0,
  minPoints: 2, // Minimum points to form a cluster
};

/**
 * Creates a supercluster index from tracking points.
 *
 * @param {Array} points - Array of tracking points (with lat, lng properties)
 * @param {Object} options - Optional supercluster configuration
 * @returns {Object} - Supercluster instance with helper methods
 */
export function createTrackingCluster(points, options = {}) {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  const index = new Supercluster({
    radius: opts.radius,
    maxZoom: opts.maxZoom,
    minZoom: opts.minZoom,
    minPoints: opts.minPoints,
  });

  // Convert tracking points to GeoJSON features for supercluster
  const features = (points || [])
    .filter((p) => p?.lat != null && p?.lng != null)
    .map((point, idx) => ({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [point.lng, point.lat],
      },
      properties: {
        ...point,
        _index: idx,
      },
    }));

  index.load(features);

  return index;
}

/**
 * Gets clusters for the current map viewport.
 *
 * @param {Object} index - Supercluster instance from createTrackingCluster()
 * @param {Array} bbox - Map bounds as [westLng, southLat, eastLng, northLat]
 * @param {number} zoom - Current map zoom level (integer)
 * @returns {Array} - Array of cluster/point features from supercluster
 */
export function getVisibleClusters(index, bbox, zoom) {
  if (!index) return [];
  return index.getClusters(bbox, Math.floor(zoom));
}

/**
 * Selects the raw point markers drawn in an overview. The initial fit must use
 * every valid point; subsequent renders may limit raw markers to individual
 * points in the current clustered viewport.
 */
export function selectOverviewPointsToDraw(points = [], visibleClusters = [], fitAll = false) {
  if (fitAll || visibleClusters.length === 0) return points;

  const visiblePointKeys = new Set(
    visibleClusters
      .filter((feature) => !isCluster(feature))
      .map((feature) => feature?.properties?.plate_number || feature?.properties?.imei)
  );

  return points.filter((point) =>
    visiblePointKeys.has(point?.plate_number || point?.imei)
  );
}

/**
 * Adds one zoom level of breathing room after fitting all overview markers.
 */
export function getOverviewFitZoom(fittedZoom, minZoom = 0) {
  if (!Number.isFinite(fittedZoom)) return fittedZoom;

  const lowerBound = Number.isFinite(minZoom) ? minZoom : 0;
  return Math.max(lowerBound, fittedZoom - 1);
}

/**
 * Extracts map bounds from a QIMap (OpenLayers) instance.
 * @param {Object} mapRef - QIMap ref
 * @returns {{ bbox: Array, zoom: number } | null}
 */
export function getQiMapViewport(mapRef) {
  if (!mapRef || typeof mapRef.getMapReference !== "function") return null;
  const olMap = mapRef.getMapReference();
  if (!olMap) return null;

  const view = olMap.getView();
  if (!view) return null;

  const zoom = view.getZoom();
  const extent = view.calculateExtent(olMap.getSize());

  // OL extent is in projected coords (EPSG:3857), convert to lon/lat
  const bottomLeft = toLonLat([extent[0], extent[1]]);
  const topRight = toLonLat([extent[2], extent[3]]);

  return {
    bbox: [bottomLeft[0], bottomLeft[1], topRight[0], topRight[1]],
    zoom: zoom,
  };
}

/**
 * Extracts map bounds from a Mapbox instance.
 * @param {Object} mapRef - Mapbox GL JS map instance
 * @returns {{ bbox: Array, zoom: number } | null}
 */
export function getMapboxViewport(mapRef) {
  if (!mapRef || typeof mapRef.getBounds !== "function") return null;

  const bounds = mapRef.getBounds();
  const zoom = mapRef.getZoom();

  return {
    bbox: [
      bounds.getWest(),
      bounds.getSouth(),
      bounds.getEast(),
      bounds.getNorth(),
    ],
    zoom: zoom,
  };
}

/**
 * Extracts map bounds from a Google Maps instance.
 * @param {Object} mapRef - Google Maps instance
 * @returns {{ bbox: Array, zoom: number } | null}
 */
export function getGoogleMapViewport(mapRef) {
  if (!mapRef || typeof mapRef.getBounds !== "function") return null;

  const bounds = mapRef.getBounds();
  if (!bounds) return null;

  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const zoom = mapRef.getZoom();

  return {
    bbox: [sw.lng(), sw.lat(), ne.lng(), ne.lat()],
    zoom: zoom,
  };
}

/**
 * Universal helper to get viewport info based on map provider.
 * @param {Object} mapRef - Map reference
 * @param {string} mapProvider - "qiMap", "mapbox", or "google"
 * @returns {{ bbox: Array, zoom: number } | null}
 */
export function getMapViewport(mapRef, mapProvider) {
  if (!mapProvider || mapProvider === "qiMap") {
    return getQiMapViewport(mapRef);
  } else if (mapProvider === "mapbox") {
    return getMapboxViewport(mapRef);
  } else if (mapProvider === "google") {
    return getGoogleMapViewport(mapRef);
  }
  return null;
}

/**
 * Determines if a feature from supercluster is a cluster or an individual point.
 * @param {Object} feature - GeoJSON feature from supercluster
 * @returns {boolean}
 */
export function isCluster(feature) {
  return feature?.properties?.cluster === true;
}

/**
 * Gets the expansion zoom for a cluster (zoom level where it breaks apart).
 * @param {Object} index - Supercluster instance
 * @param {number} clusterId - Cluster ID
 * @returns {number} - Zoom level
 */
export function getClusterExpansionZoom(index, clusterId) {
  try {
    return index.getClusterExpansionZoom(clusterId) * 1.25;
  } catch (e) {
    return 16;
  }
}

/**
 * Gets all leaves (individual points) in a cluster.
 * @param {Object} index - Supercluster instance
 * @param {number} clusterId - Cluster ID
 * @param {number} limit - Max number of leaves to return
 * @returns {Array} - Array of GeoJSON features
 */
export function getClusterLeaves(index, clusterId, limit = 100) {
  try {
    return index.getLeaves(clusterId, limit);
  } catch (e) {
    return [];
  }
}
